import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query'
import type { RootState } from '../store'

// 기본 쿼리 설정
const baseQuery = fetchBaseQuery({
  baseUrl: '/api',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    // Content-Type은 fetchBaseQuery가 자동 처리
    // - JSON body → application/json 자동 설정
    // - FormData body → 브라우저가 boundary 포함 자동 설정
    return headers
  },
})

// 토큰 갱신을 처리하는 베이스 쿼리
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await baseQuery(args, api, extraOptions)

  // 401 에러 시 토큰 갱신 시도
  if (result.error && result.error.status === 401) {
    const state = api.getState() as RootState
    const refreshToken = state.auth.refreshToken

    if (refreshToken) {
      // 토큰 갱신 요청
      const refreshResult = await baseQuery(
        {
          url: '/auth/refresh',
          method: 'POST',
          body: { refreshToken },
        },
        api,
        extraOptions
      )

      if (refreshResult.data) {
        // 새 토큰 저장
        const { accessToken, refreshToken: newRefreshToken } = refreshResult.data as {
          accessToken: string
          refreshToken: string
        }

        // authSlice의 setCredentials 액션을 import하지 않고 직접 dispatch
        api.dispatch({
          type: 'auth/updateTokens',
          payload: { accessToken, refreshToken: newRefreshToken },
        })

        // 원래 요청 재시도
        result = await baseQuery(args, api, extraOptions)
      } else {
        // 갱신 실패 시 로그아웃 + 로그인 페이지 이동
        api.dispatch({ type: 'auth/logout' })
        window.location.href = '/login'
      }
    } else {
      // 리프레시 토큰 없으면 로그아웃 + 로그인 페이지 이동
      api.dispatch({ type: 'auth/logout' })
      window.location.href = '/login'
    }
  }

  return result
}

// API 슬라이스 생성
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Board',
    'BoardContent',
    'User',
    'Manager',
    'Member',
    'Menu',
    'MenuAuth',
    'Code',
    'MasterCode',
    'SubCode',
    'Authority',
    'Banner',
    'Popup',
    'HtmlContent',
    'PrivacyPolicy',
    'Faq',
    'Statistics',
    'Dashboard',
    'Program',
    'ProgramApplicant',
    'Facility',
    'Survey',
    'SurveyQuestion',
    'SurveyUser',
    'Log',
    'Care',
    'CareFacility',
    'Welfare',
    'ChatbotDashboard',
    'ChatbotDocument',
    'ChatbotCategory',
    'ChatbotAuditLog',
    'ChatbotUsage',
    'ChatbotPlayground',
    'ChatbotSettings',
    'ChatbotQna',
    'ChatbotQnaCategory',
    'ChatbotQnaTag',
    'CommFile',
    'NoShow',
    'School',
    'Provider',
    'RecommendProgram',
    'CareService',
    'InstrRecr',
    'MentorRecr',
    'MentorAply',
  ],
  endpoints: () => ({}),
})
